/*
 * kp-accordion.js — the viewport follows the row you just opened.
 *
 * The accordion itself stays what it was: native `<details name="...">`, no
 * script, one row open at a time by the browser's own rule. This file adds
 * nothing to that state machine and the accordion is fully usable without it.
 *
 * What it fixes is a viewport problem the browser will not fix for us.
 * Opening a row closes its sibling, and if that sibling was ABOVE the scroll
 * position its content vanishes out from under you. Measured on the PDP at
 * 430x932: closing SPECIFICATIONS (670px of table) to open SHIPPING & RETURNS
 * moved that row's heading 671px upward, landing it 239px above the top of the
 * viewport — the row you asked for opens completely off screen, and the next
 * section fills the display instead.
 *
 * Scroll anchoring is the feature that is supposed to prevent exactly this,
 * and it does not run here. Five variants were measured on the live page —
 * `overflow-anchor: auto` forced on every ancestor, `overflow-anchor: none` on
 * the panel, on `::details-content`, the transition removed entirely — all
 * gave the identical -239. A minimal synthetic page with no theme CSS at all
 * reproduced it too. Chromium simply does not anchor across an exclusive
 * <details> toggle, and no CSS property turns that back on.
 *
 * `scroll-margin-top` alone is a non-fix for the same reason: it modifies a
 * scroll that something else initiates, and nothing here initiates one. It is
 * the companion to this file, not an alternative — kp-accordion.css sets it so
 * the heading clears the sticky header rather than hiding beneath it.
 */
(() => {

/*
 * Registered once per document, not once per execution of this file.
 *
 * The sibling designs row (#48) makes Dawn's product-swap path live on the
 * PDP, and HTMLUpdateUtility.setInnerHTML re-injects every <script> inside
 * <main> — so this file runs again on every swap. All four listeners below
 * are delegated on `document` and match with closest(), so one registration
 * already covers the DOM a swap brings in; a second one gives every copy its
 * own `armed` closure and every accordion press then runs follow() N times,
 * N smooth scrolls racing to the same target. Measured: this file added two
 * document listeners per swap before the guard.
 */
if (window.kpAccordionInitialised) return;
window.kpAccordionInitialised = true;
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function follow(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    const box = summary.getBoundingClientRect();
    const panelBottom = details.getBoundingClientRect().bottom;

    // The header line the summary has to clear. scroll-margin-top is the one
    // place that number lives, so it is read rather than repeated here.
    const margin = parseFloat(getComputedStyle(summary).scrollMarginTop) || 0;

    // A row that opened somewhere comfortable is left alone. Only scroll when
    // the heading has gone under the header, or the row runs past the fold.
    if (box.top >= margin && panelBottom <= window.innerHeight) return;

    // When kp-lenis is driving the window, a native smooth scroll and Lenis's
    // own animation fight over the same scrollTop and the page judders. Lenis
    // never runs under reduced motion or on touch, so the native path is
    // what those get.
    const lenis = window.__lenis;
    if (lenis && !REDUCED.matches) {
      lenis.scrollTo(summary, { offset: -margin });
      return;
    }

    summary.scrollIntoView({
      block: 'start',
      behavior: REDUCED.matches ? 'auto' : 'smooth',
    });
  }

  /*
   * Timing is the whole trick. `toggle` fires at t=0, while the closing row is
   * still at full height — scrolling then targets a position that 670px of
   * collapse immediately invalidates, and measured it lands at -596, WORSE
   * than the bug. So the scroll waits for the collapse to settle.
   *
   * transitionend rather than a literal matching --kp-dur-reveal: a duplicated
   * duration is a duration that will drift. The timeout is only a safety net
   * for browsers with no ::details-content transition, where the layout is
   * instant anyway and there is nothing to wait for.
   */
  function settleThen(details, run) {
    const group = groupName(details);
    let done = false;

    const once = () => {
      if (done) return;
      done = true;
      document.removeEventListener('transitionend', onEnd, true);
      run();
    };

    /*
     * Scoped to this exclusive group, not just to a property name. The PDP
     * also runs kp-pdp-tech's feature tables, and those animate `height` with
     * the same --kp-dur-reveal. An unscoped listener would let one of those
     * finishing nearby resolve this wait early — scrolling before the sibling
     * row has collapsed, which lands at -596px: worse than the bug above.
     */
    const onEnd = (event) => {
      if (event.propertyName !== 'block-size' && event.propertyName !== 'height') return;
      const row = event.target;
      if (!(row instanceof HTMLDetailsElement)) return;
      if (!row.classList.contains('kp-accordion')) return;
      if (groupName(row) !== group) return;
      once();
    };

    document.addEventListener('transitionend', onEnd, true);
    setTimeout(once, 500);
  }

  // `details.name` is a recent IDL attribute. The ATTRIBUTE is what makes the
  // group exclusive and is understood further back, so read that.
  function groupName(details) {
    return details.getAttribute('name');
  }

  /*
   * Only a row the visitor opened is followed. `toggle` also fires for every
   * <details open> the page LOADS with — the browser queues it while parsing,
   * and it lands after this deferred script has attached its listener. On the
   * PDP that is the FAQ's first row, well below the fold, so a fresh page
   * (any "View cue" click) scrolled itself down to the FAQ. The theme editor
   * re-rendering a section fires the same load-time toggles.
   *
   * So the summary the visitor pressed is remembered first, and a toggle is
   * acted on only when it arrives on that row within a moment of the press.
   * A row the browser opens by itself — on load, or to show an in-page find
   * match, which it scrolls to anyway — is left where it is.
   */
  let armed = null;
  let armedAt = 0;

  function arm(event) {
    const summary = event.target.closest && event.target.closest('summary');
    if (!summary) return;
    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.classList.contains('kp-accordion')) return;
    armed = details;
    armedAt = event.timeStamp;
  }

  document.addEventListener('pointerdown', arm, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') arm(event);
  }, true);

  /*
   * A link to a row — the buy box's trust line points "30-day returns" at
   * the SHIPPING & RETURNS row — used to be a plain hash jump. The browser
   * scrolled the closed <details> to the very top of the window, under the
   * sticky header, and did not open it; on a page with Lenis the jump and
   * the smooth scroll then fought over scrollTop. So the link is taken over:
   * the row opens (the group closes its sibling itself), the viewport
   * follows exactly as it does for a press, focus lands on the summary so
   * the keyboard is where the eye is, and the address bar still gets the
   * hash — without the jump.
   */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[href*="#"]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
    if (!url.hash || url.hash.length < 2) return;

    let target;
    try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (e) { return; }
    if (!(target instanceof HTMLDetailsElement) || !target.classList.contains('kp-accordion')) return;

    event.preventDefault();
    history.replaceState(history.state, '', url.hash);

    const summary = target.querySelector('summary');
    if (summary) summary.focus({ preventScroll: true });

    if (target.open) {
      // Already open: nothing collapses, so there is nothing to wait for.
      follow(target);
      return;
    }
    // Arming it lets the toggle handler below treat this like a press.
    armed = target;
    armedAt = performance.now();
    target.open = true;
  }, true);

  // `toggle` does not bubble, so the listener has to be in the capture phase.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.open || !details.classList.contains('kp-accordion')) return;
      // Only the exclusive groups can pull the page out from under you: a row
      // with no `name` closes nothing when it opens.
      if (!groupName(details)) return;
      if (details !== armed || event.timeStamp - armedAt > 1000) return;
      armed = null;

      settleThen(details, () => follow(details));
    },
    true
  );
})();
